import { useState } from "react";
import Box from "@mui/material/Box";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import { formatBRL, formatDateBR } from "../../shared/ui/format";
import ReasonDialog from "./ReasonDialog";
import ReasonPicker from "./ReasonPicker";
import { useReasonCapture } from "./useReasonCapture";
import { FrictionCapture } from "./types";

export type PaymentConfirmDialogProps = {
  row: { total_pay_value?: number | string | null; payment_date?: string | null };
  context?: string;
  busy: boolean;
  error?: string | null;
  onConfirm: (friction: FrictionCapture) => void;
  onClose: () => void;
};

// Escolha marcada cheia (mockup G1.1): o "1 clique obrigatório" precisa ficar evidente.
const toggleSx = {
  px: 2.5,
  textTransform: "none",
  fontWeight: 600,
  "&.Mui-selected, &.Mui-selected:hover": { bgcolor: "primary.main", color: "primary.contrastText" },
} as const;

/** Janela (d): "Houve atrito?" ao pagar (CA-F1a-04). Sem a linha "Forma" (P13: o BE não expõe o PIX). */
export default function PaymentConfirmDialog({ row, context, busy, error, onConfirm, onClose }: PaymentConfirmDialogProps) {
  const [has, setHas] = useState<boolean | null>(null);
  const capture = useReasonCapture("friction", { multiple: true, enabled: has === true });
  const valid = has === false || (has === true && capture.valid);

  const confirm = () => {
    if (!valid) return;
    onConfirm(has ? capture.toFriction() : { has: false });
  };

  return (
    <ReasonDialog
      title="Confirmar pagamento"
      context={context}
      size="sm"
      confirmLabel="Confirmar pagamento"
      confirmIcon={<PaymentsOutlinedIcon />}
      busy={busy}
      error={error}
      valid={valid}
      onConfirm={confirm}
      onClose={onClose}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: 0.5, fontSize: 14 }}>
        <Typography sx={{ fontSize: 14, color: "text.secondary" }}>Valor a pagar</Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{formatBRL(row.total_pay_value)}</Typography>
        <Typography sx={{ fontSize: 14, color: "text.secondary" }}>Data do pagamento</Typography>
        <Typography sx={{ fontSize: 14 }}>{formatDateBR(row.payment_date, "—")}</Typography>
      </Box>
      <Box>
        <Typography id="friction-question" sx={{ fontSize: 14, fontWeight: 500, mb: 0.75 }}>
          Houve atrito neste pacote?{" "}
          <Box component="span" aria-hidden sx={{ color: "error.main" }}>
            *
          </Box>
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          aria-labelledby="friction-question"
          value={has === null ? null : has ? "yes" : "no"}
          onChange={(_, v) => v && setHas(v === "yes")}
        >
          <ToggleButton value="no" sx={toggleSx}>
            Não
          </ToggleButton>
          <ToggleButton value="yes" sx={toggleSx}>
            Sim
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>1 clique obrigatório. Escolha um ou mais motivos.</Typography>
      </Box>
      {has === true && <ReasonPicker capture={capture} label="Motivos do atrito" />}
    </ReasonDialog>
  );
}
